<template>
  <div>
    <SoAlert
      v-if="query?.isError"
      type="error"
      :title="errorTitle"
      :data-testid="`${dataTestid}-error`"
    />
    <SoDataTableServer
      v-else
      :headers="headers"
      :data="tableData"
      :options="tableOptions"
      :default-items-per-page="25"
      :searchable="searchable"
      :search-align-end="searchAlignEnd"
      :search-placeholder="searchPlaceholder"
      :row-clickable="rowClickable"
      :data-testid="`${dataTestid}-table`"
      @fetch="fetchData"
      @click:row="$emit('rowClick', $event)"
    >
      <!-- Forward any cell slots (e.g. #item.status) to the underlying table. -->
      <template v-for="(_, name) in $slots" #[name]="slotData">
        <slot :name="name" v-bind="slotData ?? {}" />
      </template>
    </SoDataTableServer>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoDataTableServer, {
    SoDataTableData,
    SoDataTableOptions,
  } from "@/components/dataTable/SoDataTableServer.vue"
  import {
    SoDataTableHeader,
    SoDataTableItem,
    SoDataTableOrderBy,
    SoDataTableFilters,
  } from "@/types/components/SoDataTable"
  import { QueryResult } from "@/api/utils/types"

  /**
   * The shape every warrants list service resolves to: a total count plus a
   * (differently-named) array of rows. Each tab supplies an `itemsFrom` that
   * pulls its own rows out of the concrete result.
   */
  export interface WarrantsTableListResult {
    totalCount: number
  }

  /**
   * Shared shell for the warrants admin data-table tabs. Owns the server-side
   * query lifecycle (loading / error / rows) so each tab only declares its
   * headers, error copy and how to fetch + read its rows.
   */
  export default defineComponent({
    name: "WarrantsDataTableTab",
    components: {
      SoAlert,
      SoDataTableServer,
    },
    props: {
      headers: {
        type: Array as PropType<SoDataTableHeader[]>,
        required: true,
      },
      errorTitle: {
        type: String,
        required: true,
      },
      dataTestid: {
        type: String,
        required: true,
      },
      // `options`/`data` params are widened to `never` so each tab can keep its
      // own precise `SoDataTableOptions<Filters>` / result types on the callback
      // it passes in (a wider param here would reject those narrower signatures).
      // The shell re-supplies the real values, so the coercions are safe.
      fetcher: {
        type: Function as PropType<
          (options: never) => QueryResult<WarrantsTableListResult>
        >,
        required: true,
      },
      // Rows come back as `unknown[]` and are coerced to table items here, so
      // tabs stay cast-free.
      itemsFrom: {
        type: Function as PropType<(data: never) => unknown[]>,
        required: true,
      },
      searchable: {
        type: Boolean,
        default: false,
      },
      searchAlignEnd: {
        type: Boolean,
        default: false,
      },
      searchPlaceholder: {
        type: String,
        default: "Search",
      },
      rowClickable: {
        type: Boolean,
        default: false,
      },
      // Newest rows first by default; override per tab where needed.
      defaultOrderBy: {
        type: Object as PropType<SoDataTableOrderBy>,
        default: () => ({ id: "desc" }),
      },
      filters: {
        type: Object as PropType<SoDataTableFilters>,
        default: undefined,
      },
    },
    emits: ["rowClick"],
    data() {
      return {
        query: null as QueryResult<WarrantsTableListResult> | null,
      }
    },
    computed: {
      tableOptions(): Partial<SoDataTableOptions> {
        const options: Partial<SoDataTableOptions> = {
          orderBy: this.defaultOrderBy,
        }
        if (this.filters) {
          options.filters = this.filters
        }
        return options
      },
      tableData(): SoDataTableData {
        const data = this.query?.data
        return {
          isLoading: !!this.query?.isFetching,
          totalItems: data?.totalCount ?? 0,
          // `data` is narrowed by each tab's own `itemsFrom` (see prop docs).
          items: data
            ? (this.itemsFrom(data as never) as SoDataTableItem[])
            : [],
        }
      },
    },
    methods: {
      fetchData(options: SoDataTableOptions) {
        this.query = this.fetcher(options as never)
      },
    },
  })
</script>
